import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Paper, Button, TextField, IconButton, Typography, Popper, } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getLocalTimeZone, today, startOfMonth, startOfWeek, } from '@internationalized/date';
import './singleDatePicker.css';
import { TextInput } from '../FormInputs';
const SingleDatePicker = ({ value = null, onChange = () => { }, placeholder = 'Select Date', maxPastYears = 3, disableFuture = false, minDate = null, allowToday = false, disabled = false, label, required = false, error = false, errorMessage, helperText, fullWidth = true, className = '', showActions = false, // ✅ default false
enabledDates, }) => {
    // O(1) lookup of allowed dates (YYYY-MM-DD); undefined => no restriction.
    const enabledSet = React.useMemo(() => (enabledDates ? new Set(enabledDates) : null), [enabledDates]);
    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = today(getLocalTimeZone());
        return startOfMonth(now);
    });
    const [tempDate, setTempDate] = useState(value);
    const anchorRef = useRef(null);
    const popperRef = useRef(null);
    useEffect(() => {
        setTempDate(value);
    }, [value]);
    useEffect(() => {
        if (value) {
            setCurrentMonth(startOfMonth(value));
        }
    }, [value]);
    const handleOpen = () => {
        if (disabled)
            return;
        setIsOpen(true);
    };
    const handleClose = () => {
        setIsOpen(false);
        if (showActions) {
            setTempDate(value);
        }
    };
    const handleApply = () => {
        onChange(tempDate);
        setIsOpen(false);
    };
    const handleCancel = () => {
        handleClose();
    };
    const handleDateClick = (date) => {
        setTempDate(date);
        if (!showActions) {
            onChange(date);
            setIsOpen(false);
        }
    };
    const popperContentRef = useRef(null);
    useEffect(() => {
        if (!isOpen)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (anchorRef.current?.contains(target) ||
                popperContentRef.current?.contains(target)) {
                return;
            }
            handleClose();
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isOpen, value, showActions]);
    const navigateMonth = useCallback((direction) => {
        setCurrentMonth((prev) => {
            const currentDate = today(getLocalTimeZone());
            const minAllowedDate = currentDate.subtract({ years: maxPastYears });
            const newMonth = prev.add({ months: direction });
            if (direction < 0 && newMonth.compare(startOfMonth(minAllowedDate)) < 0) {
                return prev;
            }
            return newMonth;
        });
    }, [maxPastYears]);
    const isPreviousDisabled = useCallback(() => {
        const currentDate = today(getLocalTimeZone());
        const minAllowedDate = currentDate.subtract({ years: maxPastYears });
        const minAllowedMonth = startOfMonth(minAllowedDate);
        return currentMonth.compare(minAllowedMonth) <= 0;
    }, [currentMonth, maxPastYears]);
    const isDateDisabled = useCallback((date) => {
        const now = today(getLocalTimeZone());
        if (!allowToday && date.compare(now) === 0)
            return true;
        if (disableFuture && date.compare(now) > 0)
            return true;
        if (minDate && date.compare(minDate) < 0)
            return true;
        // Outside the allow-list (e.g. days with no data) → faded/disabled.
        if (enabledSet) {
            const ymd = `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
            if (!enabledSet.has(ymd))
                return true;
        }
        return false;
    }, [disableFuture, allowToday, minDate, enabledSet]);
    const renderCalendar = useCallback(() => {
        const start = startOfMonth(currentMonth);
        const calendarStart = startOfWeek(start, 'en-US');
        const days = [];
        let current = calendarStart;
        for (let i = 0; i < 42; i++) {
            days.push(current);
            current = current.add({ days: 1 });
        }
        const monthName = new Date(currentMonth.year, currentMonth.month - 1, currentMonth.day).toLocaleString('en-US', { month: 'long', year: 'numeric' });
        const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
        return (_jsxs(Box, { className: "single-calendar-container", children: [_jsxs(Box, { className: "calendar-header", children: [_jsx(IconButton, { size: "small", onClick: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigateMonth(-1);
                            }, disabled: isPreviousDisabled(), className: "calendar-nav-button", children: _jsx(ChevronLeft, {}) }), _jsx(Typography, { variant: "body1", className: "calendar-month-name", children: monthName }), _jsx(IconButton, { size: "small", onClick: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigateMonth(1);
                            }, className: "calendar-nav-button", children: _jsx(ChevronRight, {}) })] }), _jsxs(Box, { className: "calendar-grid", children: [weekDays.map((day) => (_jsx(Box, { className: "calendar-weekday", children: day }, day))), days.map((date, idx) => {
                            const isCurrentMonth = date.month === currentMonth.month;
                            const isSelected = tempDate &&
                                date.year === tempDate.year &&
                                date.month === tempDate.month &&
                                date.day === tempDate.day;
                            const isDisabled = isDateDisabled(date);
                            return (_jsx(Box, { className: `calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled-date' : ''}`, onClick: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!isDisabled && isCurrentMonth) {
                                        handleDateClick(date);
                                    }
                                }, children: date.day }, idx));
                        })] })] }));
    }, [currentMonth, tempDate, navigateMonth, isPreviousDisabled, isDateDisabled]);
    const formatDate = useCallback((date) => {
        if (!date)
            return '';
        const mm = String(date.month).padStart(2, '0');
        const dd = String(date.day).padStart(2, '0');
        const yyyy = date.year;
        return `${mm}/${dd}/${yyyy}`;
    }, []);
    const displayValue = formatDate(value);
    return (_jsxs(Box, { className: `single-date-picker-wrapper ${fullWidth ? 'full-width' : ''} ${className}`, children: [_jsx(Box, { className: "single-date-picker-anchor", ref: anchorRef, children: _jsx("div", { onClick: handleOpen, className: disabled ? 'single-date-picker-disabled' : '', children: _jsx(TextInput, { label: label, required: required, error: error, errorMessage: errorMessage, helperText: helperText, disabled: disabled, fullWidth: fullWidth, value: displayValue, placeholder: placeholder, readOnly: true, rightIcon: _jsx("svg", { width: "18", height: "18", viewBox: "0 0 18 18", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M12.75 1.5C12.75 1.08579 12.4142 0.75 12 0.75C11.5858 0.75 11.25 1.08579 11.25 1.5H12H12.75ZM11.25 4.5C11.25 4.91421 11.5858 5.25 12 5.25C12.4142 5.25 12.75 4.91421 12.75 4.5H12H11.25ZM6.75 1.5C6.75 1.08579 6.41421 0.75 6 0.75C5.58579 0.75 5.25 1.08579 5.25 1.5H6H6.75ZM5.25 4.5C5.25 4.91421 5.58579 5.25 6 5.25C6.41421 5.25 6.75 4.91421 6.75 4.5H6H5.25ZM2.25 6.75C1.83579 6.75 1.5 7.08579 1.5 7.5C1.5 7.91421 1.83579 8.25 2.25 8.25V7.5V6.75ZM15.75 8.25C16.1642 8.25 16.5 7.91421 16.5 7.5C16.5 7.08579 16.1642 6.75 15.75 6.75V7.5V8.25ZM3.75 3V3.75H14.25V3V2.25H3.75V3ZM14.25 3V3.75C14.6642 3.75 15 4.08579 15 4.5H15.75H16.5C16.5 3.25736 15.4926 2.25 14.25 2.25V3ZM15.75 4.5H15V15H15.75H16.5V4.5H15.75ZM15.75 15H15C15 15.4142 14.6642 15.75 14.25 15.75V16.5V17.25C15.4926 17.25 16.5 16.2426 16.5 15H15.75ZM14.25 16.5V15.75H3.75V16.5V17.25H14.25V16.5ZM3.75 16.5V15.75C3.33579 15.75 3 15.4142 3 15H2.25H1.5C1.5 16.2426 2.50736 17.25 3.75 17.25V16.5ZM2.25 15H3V4.5H2.25H1.5V15H2.25ZM2.25 4.5H3C3 4.08579 3.33579 3.75 3.75 3.75V3V2.25C2.50736 2.25 1.5 3.25736 1.5 4.5H2.25ZM12 1.5H11.25V4.5H12H12.75V1.5H12ZM6 1.5H5.25V4.5H6H6.75V1.5H6ZM2.25 7.5V8.25H15.75V7.5V6.75H2.25V7.5Z", fill: "#71717A" }) }), className: `single-date-picker-input ${isOpen ? 'picker-open' : ''}` }) }) }), _jsx(Popper, { open: isOpen, anchorEl: anchorRef.current, placement: "bottom-start", className: "single-date-picker-popper", ref: popperRef, modifiers: [
                    {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                            altAxis: true,
                            altBoundary: true,
                            tether: true,
                            rootBoundary: 'viewport',
                            padding: 8,
                        },
                    },
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 6],
                        },
                    },
                ], children: _jsx(Paper, { ref: popperContentRef, className: "single-date-picker-modal", elevation: 8, children: _jsx(Box, { className: "single-date-picker-content", children: _jsx(Box, { className: "single-date-picker-body", children: _jsxs(Box, { className: "single-date-picker-main", children: [showActions && (_jsx(Box, { className: "single-date-picker-input-preview", children: _jsx(TextField, { value: formatDate(tempDate), placeholder: placeholder, InputProps: {
                                                readOnly: true,
                                                startAdornment: (_jsx("div", { className: "d-flex", children: _jsx("svg", { className: "input-icon", width: "18", height: "18", viewBox: "0 0 18 18", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M12.75 1.5C12.75 1.08579 12.4142 0.75 12 0.75C11.5858 0.75 11.25 1.08579 11.25 1.5H12H12.75ZM11.25 4.5C11.25 4.91421 11.5858 5.25 12 5.25C12.4142 5.25 12.75 4.91421 12.75 4.5H12H11.25ZM6.75 1.5C6.75 1.08579 6.41421 0.75 6 0.75C5.58579 0.75 5.25 1.08579 5.25 1.5H6H6.75ZM5.25 4.5C5.25 4.91421 5.58579 5.25 6 5.25C6.41421 5.25 6.75 4.91421 6.75 4.5H6H5.25ZM2.25 6.75C1.83579 6.75 1.5 7.08579 1.5 7.5C1.5 7.91421 1.83579 8.25 2.25 8.25V7.5V6.75ZM15.75 8.25C16.1642 8.25 16.5 7.91421 16.5 7.5C16.5 7.08579 16.1642 6.75 15.75 6.75V7.5V8.25ZM3.75 3V3.75H14.25V3V2.25H3.75V3ZM14.25 3V3.75C14.6642 3.75 15 4.08579 15 4.5H15.75H16.5C16.5 3.25736 15.4926 2.25 14.25 2.25V3ZM15.75 4.5H15V15H15.75H16.5V4.5H15.75ZM15.75 15H15C15 15.4142 14.6642 15.75 14.25 15.75V16.5V17.25C15.4926 17.25 16.5 16.2426 16.5 15H15.75ZM14.25 16.5V15.75H3.75V16.5V17.25H14.25V16.5ZM3.75 16.5V15.75C3.33579 15.75 3 15.4142 3 15H2.25H1.5C1.5 16.2426 2.50736 17.25 3.75 17.25V16.5ZM2.25 15H3V4.5H2.25H1.5V15H2.25ZM2.25 4.5H3C3 4.08579 3.33579 3.75 3.75 3.75V3V2.25C2.50736 2.25 1.5 3.25736 1.5 4.5H2.25ZM12 1.5H11.25V4.5H12H12.75V1.5H12ZM6 1.5H5.25V4.5H6H6.75V1.5H6ZM2.25 7.5V8.25H15.75V7.5V6.75H2.25V7.5Z", fill: "#71717A" }) }) })),
                                            }, className: "range-input-field active-input" }) })), _jsx(Box, { className: "calendars-container single-calendar", children: renderCalendar() }), showActions && (_jsxs(Box, { className: "date-picker-actions", children: [_jsx(Button, { onClick: handleCancel, className: "cancel-button", children: "Cancel" }), _jsx(Button, { onClick: handleApply, className: "apply-button", children: "Apply" })] }))] }) }) }) }) })] }));
};
export default SingleDatePicker;
